import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface KaleidoscopeOption {
  id: string;
  title: string;
  description?: string;
  video_path: string;
  audio_path?: string;
  duration: number;
  thumbnail_path?: string;
  is_active: boolean;
}

interface KaleidoscopeSelectorProps {
  onSelect: (option: KaleidoscopeOption) => void;
  onSkip: () => void;
}

export default function KaleidoscopeSelector({ onSelect, onSkip }: KaleidoscopeSelectorProps) {
  const [options, setOptions] = useState<KaleidoscopeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState<KaleidoscopeOption | null>(null);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    loadKaleidoscopeOptions();
  }, []);

  const loadKaleidoscopeOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('kaleidoscope_options')
        .select('*')
        .eq('is_active', true)
        .order('title');

      if (error) throw error;

      setOptions(data || []);

      // Load thumbnails
      if (data && data.length > 0) {
        const urls: Record<string, string> = {};
        await Promise.all(
          data.map(async (option) => {
            if (option.thumbnail_path) {
              const { data: urlData } = await supabase.storage
                .from('kaleidoscope-thumbnails')
                .createSignedUrl(option.thumbnail_path, 3600);
              if (urlData?.signedUrl) {
                urls[option.id] = urlData.signedUrl;
              }
            }
          })
        );
        setThumbnailUrls(urls);
      }
    } catch (error) {
      console.error('Error loading kaleidoscope options:', error);
      toast({
        title: "Error loading options",
        description: "Failed to load kaleidoscope options. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (option: KaleidoscopeOption) => {
    setSelectedOption(option);
    onSelect(option);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading kaleidoscope options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mystical text-foreground">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-primary mr-3" />
            <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Choose Your Hypnotic Induction
            </h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Select a kaleidoscope pattern to begin your manifestation journey with a hypnotic induction phase.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {options.map((option) => (
            <Card
              key={option.id}
              className={`relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-soft border-2 ${
                selectedOption?.id === option.id 
                  ? 'border-primary shadow-soft' 
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => handleSelect(option)}
            >
              <div className="aspect-video relative bg-gradient-to-br from-primary/20 to-accent/20">
                {thumbnailUrls[option.id] ? (
                  <img
                    src={thumbnailUrls[option.id]}
                    alt={option.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                    <Sparkles className="w-12 h-12 text-primary/50" />
                  </div>
                )}
                
                {/* Play overlay */}
                <div className="absolute inset-0 bg-background/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-foreground/90 rounded-full p-3">
                    <Play className="w-6 h-6 text-background fill-current" />
                  </div>
                </div>

                {/* Duration badge */}
                <Badge className="absolute top-2 right-2 bg-background/70 text-foreground">
                  <Clock className="w-3 h-3 mr-1" />
                  {option.duration}s
                </Badge>
              </div>

              <div className="p-4">
                <h3 className="font-semibold text-lg mb-2 text-foreground">{option.title}</h3>
                {option.description && (
                  <p className="text-muted-foreground text-sm">{option.description}</p>
                )}
              </div>
            </Card>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={onSkip}
            variant="outline"
            size="lg"
            className="border-primary/40 text-primary hover:bg-primary/10"
          >
            Skip Hypnotic Induction
          </Button>
          
          {selectedOption && (
            <Button
              onClick={() => onSelect(selectedOption)}
              size="lg"
              className="bg-gradient-primary hover:bg-primary text-primary-foreground shadow-soft"
            >
              <Play className="w-5 h-5 mr-2" />
              Start with {selectedOption.title}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}